/**
 * Observes the position of an element, enabling lazy-loading for images
 * and optimising rendering with `content-visibility: auto`.
 */

/**
 * Sets content-visibility optimisations for web components.
 * 1. Sets `content-visibility: visible` for components in the viewport.
 * 2. Sets `content-visibility: auto` for all other components.
 * 3. Disconnects the observer after the initial round.
 *
 * Additional behaviour:
 * - If the component uses `position: fixed`, it is always set to
 *   `content-visibility: visible` and is not observed.
 * - When the component enters the viewport, any `<img>` in its shadow root
 *   has its `loading` attribute removed to trigger immediate loading.
 *
 * @param {HTMLElement} element - The web component to observe.
 */
export function observePosition(element: HTMLElement): void {
	if (getComputedStyle(element).position === 'fixed') {
		element.style.contentVisibility = 'visible';
		return;
	}
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					// Component is in view: Render its content
					const img = element.shadowRoot?.querySelector('img');
					if (img) img.removeAttribute('loading'); // Trigger image loading
					element.style.contentVisibility = 'visible';
				} else {
					// Component is outside the viewport: Optimise rendering
					element.style.contentVisibility = 'auto';
				}
			});

			// Disconnect after processing all elements
			observer.disconnect();
		},
		{ root: null, threshold: 0 }, // Observe relative to the viewport
	);

	// Start observing the component
	observer.observe(element);
}
